import { useCallback, useEffect, useRef, useState } from 'react';
import { getMedicationDetailsGuest, searchMedicationsGuest } from '../api/medications';

// The logged-out medication search, shared by the composer's Medications
// panel (STACK-563) and the interaction checker page's medications field
// (STACK-587). Each draws its own UI; this is the part they must not drift
// on: the POST lookups, so what a visitor types never sits in a URL
// (docs/guest-medication-check.md §3.1), 2 characters and a 300 ms pause
// before searching, as the profile's picker does, and only the latest
// search's answer counts.

export const MEDICATION_SEARCH_DEBOUNCE_MS = 300;
export const MEDICATION_SEARCH_MIN_TERM = 2;

/**
 * @param {string} term - what the visitor has typed
 * @returns {{ query: string, results: Array, view: string, retry: Function }}
 *   `view` is idle | loading | results | none | error; `retry` reruns the
 *   current search after an error.
 */
export default function useMedicationSearch(term) {
  const [results, setResults] = useState([]);
  const [view, setView] = useState('idle');
  const [searchNonce, setSearchNonce] = useState(0);
  const searchSeq = useRef(0);
  const query = (term || '').trim();

  useEffect(() => {
    if (query.length < MEDICATION_SEARCH_MIN_TERM) {
      searchSeq.current += 1;
      setResults([]);
      setView('idle');
      return undefined;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      setView('loading');
      try {
        const found = await searchMedicationsGuest(query);
        if (seq !== searchSeq.current) return;
        setResults(found);
        setView(found.length ? 'results' : 'none');
      } catch (_) {
        if (seq !== searchSeq.current) return;
        setResults([]);
        setView('error');
      }
    }, MEDICATION_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, searchNonce]);

  const retry = useCallback(() => setSearchNonce((n) => n + 1), []);

  return { query, results, view, retry };
}

/**
 * Resolves a picked search result to what the check needs:
 * `{ in_rxcui, ingredient_name, display_name }`, or null when the result has
 * no product code or the details lookup names no ingredient. Throws when the
 * lookup fails.
 */
export const resolveMedicationPick = async (result) => {
  const rxcui = result?.rxcuis?.[0];
  if (!rxcui) return null;
  const details = await getMedicationDetailsGuest(rxcui);
  if (!details?.in_rxcui) return null;
  return {
    in_rxcui: details.in_rxcui,
    ingredient_name: details.ingredient_name || result.generic_name || '',
    display_name: result.display_name,
  };
};
